// s8 — pending_review biomarkers (вне справочника). Modal-style без BottomNav.
// Source: docs/specs/sprint-5/stage-4-frontend.md §5.4 + prototypes/index.html #s8.
//
// Two-state CTA flow (frontend-only, без backend):
//   1. default     → CTA «Сообщить — добавьте эти показатели» (без чек-боксов)
//   2. selecting   → активируются чек-боксы (все unchecked), CTA «Отправить · N из M» + «Отмена»
//   3. sent        → чек-боксы исчезают, success-сообщение вместо кнопок
// 1-marker case — тот же two-step flow без шорткатов.

import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Info, Loader2, Send } from 'lucide-react';
import { useBiomarkersExtras, useRequestAddExtraMarkers } from '@/api/hooks/biomarkers';
import { queryKeys } from '@/api/queryKeys';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { CatalogSkeleton } from '@/components/Skeletons';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { capitalizeFirst, cn } from '@/lib/utils';
import type { BiomarkerExtraItem, BiomarkerExtraPoint } from '@/api/types';

type Mode = 'default' | 'selecting' | 'sent';

export function ExtraMarkers() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useBiomarkersExtras();
  const { toast } = useToast();
  const requestAdd = useRequestAddExtraMarkers();
  const [mode, setMode] = useState<Mode>('default');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const items = data?.items ?? [];
  const totalCount = items.length;
  // Просить «добавьте в справочник» можно только для отсутствующих карточек.
  // У unit_unknown и no_range карточка есть — там правка на нашей стороне
  // (единицы / диапазон нормы), и бэкенд такие id всё равно отбросит: заявка
  // создаётся только для pending. Аллоулист, а не список исключений: новая
  // причина не должна автоматически становиться «заявкой».
  const requestable = items.filter((i) => i.reason === 'not_in_catalog');

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const cancelSelection = () => {
    setMode('default');
    setSelected(new Set());
  };

  const submit = () => {
    if (selected.size === 0 || requestAdd.isPending) return;
    requestAdd.mutate(Array.from(selected), {
      onSuccess: (resp) => {
        // Edge: ни одна строка не учтена (admin успел canonicalize / удалить
        // биомаркеры между GET extras и нашим POST). Сервер вернул 200, но
        // success-state будет misleading. Просим юзера обновить и refresh'им
        // кеш extras.
        if (resp.requested + resp.duplicated === 0) {
          toast({
            title: 'Список устарел',
            description: 'Эти показатели уже были обработаны. Обновляем список.',
            variant: 'warning',
          });
          void queryClient.invalidateQueries({ queryKey: queryKeys.biomarkersExtras });
          cancelSelection();
          return;
        }
        setMode('sent');
      },
      onError: () => {
        toast({
          title: 'Не удалось отправить',
          description: 'Проверьте соединение и попробуйте ещё раз.',
          variant: 'destructive',
        });
      },
    });
  };

  if (isPending) {
    return (
      <MobileFrame>
        <ScreenHeader onBack={() => setLocation('/markers')} title="Показатели без оценки" />
        <CatalogSkeleton />
      </MobileFrame>
    );
  }
  if (isError || !data) {
    return (
      <MobileFrame>
        <ScreenHeader onBack={() => setLocation('/markers')} title="Показатели без оценки" />
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          Не удалось загрузить список.
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <ScreenHeader
        onBack={() => setLocation('/markers')}
        title="Показатели без оценки"
      />

      {totalCount > 0 && (
        <div className="px-4 pb-2 pt-0 text-hl-small text-muted-foreground">
          {totalCount} {pluralize(totalCount, ['показатель', 'показателя', 'показателей'])} · распознаны, но не оценены
        </div>
      )}

      <main className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4 pt-2">
        {totalCount === 0 ? (
          <p className="pt-6 text-center text-sm text-muted-foreground">
            Все распознанные показатели оценены.
          </p>
        ) : (
          <>
            <div className="rounded-surface border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-2.5">
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                </div>
                <p className="text-hl-secondary leading-snug text-foreground">
                  Эти показатели <strong>распознаны из ваших анализов</strong>, но оценить их мы
                  пока не можем — поэтому не показываем норму, динамику и комментарий. Под каждым
                  написано, в чём дело.
                  {requestable.length > 0 && (
                    <> Если какой-то из них важно отслеживать — отправьте запрос, и мы добавим его
                    в одном из ближайших обновлений.</>
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {items.map((item) => (
                <ExtraMarkerCard
                  key={item.biomarker_id}
                  item={item}
                  showCheckbox={mode === 'selecting' && item.reason === 'not_in_catalog'}
                  checked={selected.has(item.biomarker_id)}
                  onToggle={() => toggle(item.biomarker_id)}
                />
              ))}
            </div>

            {mode === 'default' && requestable.length > 0 && (
              <button
                type="button"
                onClick={() => setMode('selecting')}
                className="flex w-full items-center justify-center gap-2 rounded-surface bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Сообщить — добавьте эти показатели
              </button>
            )}

            {mode === 'selecting' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={cancelSelection}
                    className="flex-1 rounded-surface border border-border bg-surface px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-muted"
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={selected.size === 0 || requestAdd.isPending}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 rounded-surface px-4 py-3 text-sm font-semibold transition',
                      selected.size === 0 || requestAdd.isPending
                        ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                  >
                    {requestAdd.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Send className="h-4 w-4" aria-hidden="true" />
                    )}
                    Отправить · {selected.size} из {requestable.length}
                  </button>
                </div>
                <p className="text-center text-hl-small text-muted-foreground">
                  Отметьте показатели, которые важны для вас
                </p>
              </div>
            )}

            {mode === 'sent' && (
              <div className="flex items-start gap-3 rounded-surface border border-primary/30 bg-primary/5 p-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground">Запрос отправлен</div>
                  <p className="mt-0.5 text-hl-small leading-snug text-muted-foreground">
                    Спасибо! Мы добавим эти показатели в одном из ближайших обновлений.
                  </p>
                </div>
              </div>
            )}

            {mode === 'default' && requestable.length > 0 && (
              <p className="text-center text-hl-small text-muted-foreground">
                Помогите нам выбрать, какие биомаркеры добавить следующими
              </p>
            )}
          </>
        )}
      </main>
    </MobileFrame>
  );
}

function ExtraMarkerCard({
  item,
  showCheckbox,
  checked,
  onToggle,
}: {
  item: BiomarkerExtraItem;
  showCheckbox: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const name = capitalizeFirst(item.canonical_name_ru ?? item.raw_name);
  const latestPoint = useMemo(() => pickLatestPoint(item.points), [item.points]);
  const dateStr = latestPoint ? formatRu(latestPoint.measured_at) : '';
  const valueText = latestPoint ? formatValue(latestPoint) : '—';
  const unitText = latestPoint?.unit_raw ?? '';

  const cardBody = (
    <>
      {showCheckbox && (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Выбрать ${name}`}
          className="mt-0.5 flex-shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">{name}</div>
        {dateStr && <div className="text-hl-small text-muted-foreground">{dateStr}</div>}
        <div className="mt-0.5 text-hl-small leading-snug text-muted-foreground">{reasonLabel(item, unitText)}</div>
      </div>
      <div className="text-right">
        <div className="text-base font-bold text-foreground">{valueText}</div>
        {unitText && <div className="text-hl-small text-muted-foreground">{unitText}</div>}
      </div>
    </>
  );

  if (showCheckbox) {
    return (
      <label
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-surface border bg-surface p-3.5 transition',
          checked ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-gray-300',
        )}
      >
        {cardBody}
      </label>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-surface border border-border bg-surface p-3.5">
      {cardBody}
    </div>
  );
}

/**
 * Почему показатель не оценён. Текст пишем от лица пользователя: «нет в справочнике»
 * — это про нас, а «норма не определена для вашего профиля» — про то, что карточка
 * есть, но диапазон в ней задан для другого пола/возраста (HEAL-30, мужские ПСА).
 */
export function reasonLabel(item: BiomarkerExtraItem, unitText: string): string {
  if (item.reason === 'unit_unknown') {
    return unitText ? `Не распознали единицу «${unitText}»` : 'В анализе не указана единица измерения';
  }
  if (item.reason === 'no_range') {
    return 'Норма не определена для вашего пола или возраста';
  }
  if (item.reason === 'qualitative_unknown') {
    // Слово, которого нет в словаре («единичные», «умеренное количество»).
    // Показываем результат как есть — пропасть он не должен.
    return 'Пока не умеем оценивать такой ответ';
  }
  return 'Нет в справочнике';
}

function pickLatestPoint(points: BiomarkerExtraPoint[]): BiomarkerExtraPoint | null {
  if (points.length === 0) return null;
  return [...points].sort((a, b) => (a.measured_at < b.measured_at ? 1 : -1))[0] ?? null;
}

function formatValue(p: BiomarkerExtraPoint): string {
  if (p.value_raw !== null) return String(p.value_raw);
  return p.value_qualitative ?? '—';
}

function formatRu(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function pluralize(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}
